/**
 * PreferencesContext
 * Global context for user preferences across the dashboard
 * Created: 2025-12-23 14:50 EST
 */

import { createContext, useContext, useState, useEffect } from 'react'

// Default sportsbooks
const ALL_SPORTSBOOKS = [
  { id: 'draftkings', name: 'DraftKings', logo: 'draftkings.png' },
  { id: 'fanduel', name: 'FanDuel', logo: 'fanduel.ico' },
  { id: 'betmgm', name: 'BetMGM', logo: 'betmgm.ico' },
  { id: 'caesars', name: 'Caesars', logo: 'caesars.png' },
  { id: 'betrivers', name: 'BetRivers', logo: 'betrivers.png' },
  { id: 'pointsbet', name: 'PointsBet', logo: 'pointsbet.png' },
  { id: 'barstool', name: 'Barstool', logo: 'barstool.png' },
  { id: 'unibet', name: 'UniBet', logo: 'unibet.png' },
  { id: 'wynnbet', name: 'WynnBet', logo: 'wynnbet.png' },
  { id: 'betfair', name: 'BetFair', logo: 'betfair.png' },
  { id: 'pinnacle', name: 'Pinnacle', logo: 'pinnacle.png' },
  { id: 'bet365', name: 'Bet365', logo: 'bet365.png' },
]

// US States where sports betting is legal (as of 2024)
const LEGAL_STATES = [
  { code: 'AZ', name: 'Arizona' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WY', name: 'Wyoming' },
]

// State to sportsbook availability mapping
const STATE_BOOKS = {
  AZ: ['draftkings', 'fanduel', 'betmgm', 'caesars', 'betrivers', 'barstool', 'wynnbet'],
  CO: ['draftkings', 'fanduel', 'betmgm', 'caesars', 'betrivers', 'pointsbet', 'barstool'],
  CT: ['draftkings', 'fanduel'],
  IL: ['draftkings', 'fanduel', 'betmgm', 'caesars', 'betrivers', 'pointsbet', 'barstool'],
  IN: ['draftkings', 'fanduel', 'betmgm', 'caesars', 'betrivers', 'pointsbet', 'barstool', 'unibet'],
  IA: ['draftkings', 'fanduel', 'betmgm', 'caesars', 'betrivers', 'pointsbet'],
  KS: ['draftkings', 'fanduel', 'betmgm', 'caesars', 'barstool'],
  KY: ['draftkings', 'fanduel', 'betmgm', 'caesars'],
  LA: ['draftkings', 'fanduel', 'betmgm', 'caesars', 'barstool'],
  MD: ['draftkings', 'fanduel', 'betmgm', 'caesars', 'betrivers', 'pointsbet', 'barstool'],
  MA: ['draftkings', 'fanduel', 'betmgm', 'caesars', 'betrivers'],
  MI: ['draftkings', 'fanduel', 'betmgm', 'caesars', 'betrivers', 'pointsbet', 'barstool', 'wynnbet'],
  NV: ['draftkings', 'betmgm', 'caesars', 'wynnbet'],
  NH: ['draftkings', 'fanduel', 'betmgm'],
  NJ: ['draftkings', 'fanduel', 'betmgm', 'caesars', 'betrivers', 'pointsbet', 'barstool', 'unibet', 'wynnbet'],
  NY: ['draftkings', 'fanduel', 'betmgm', 'caesars', 'betrivers', 'pointsbet', 'wynnbet'],
  NC: ['draftkings', 'fanduel', 'betmgm', 'caesars'],
  OH: ['draftkings', 'fanduel', 'betmgm', 'caesars', 'betrivers', 'pointsbet', 'barstool'],
  OR: ['draftkings', 'fanduel', 'betmgm'],
  PA: ['draftkings', 'fanduel', 'betmgm', 'caesars', 'betrivers', 'pointsbet', 'barstool', 'unibet'],
  TN: ['draftkings', 'fanduel', 'betmgm', 'caesars', 'betrivers', 'barstool'],
  VA: ['draftkings', 'fanduel', 'betmgm', 'caesars', 'betrivers', 'pointsbet', 'barstool', 'unibet'],
  WV: ['draftkings', 'fanduel', 'betmgm', 'caesars', 'betrivers', 'pointsbet'],
  WY: ['draftkings', 'fanduel', 'betmgm'],
  // Default - all books
  DEFAULT: ALL_SPORTSBOOKS.map(b => b.id),
}

// Default preferences
const DEFAULT_PREFERENCES = {
  // Sportsbooks
  selectedBooks: ALL_SPORTSBOOKS.map(b => b.id),
  bookOrder: ['fanduel', 'draftkings', 'betmgm', 'caesars', 'betrivers', 'pointsbet', 'barstool', 'unibet', 'wynnbet', 'betfair', 'pinnacle', 'bet365'],

  // State/Jurisdiction
  state: 'AZ',
  autoDetectState: false,

  // Odds format: 'american', 'decimal', 'fractional', 'implied'
  oddsFormat: 'american',

  // Unit size / Bankroll
  unitSize: 100,
  bankroll: 1000,

  // Notifications
  notifications: {
    arbitrage: false,
    lineMovement: false,
    gameStart: false,
    emailNotifications: false,
  },

  // Display
  theme: 'dark',
  compactView: false,
  hiddenColumns: [],
  showImpliedProbability: true,  // Show implied probability tooltip on odds hover
}

const PreferencesContext = createContext(null)

export function PreferencesProvider({ children }) {
  const [preferences, setPreferences] = useState(() => {
    // Load from localStorage on init
    const saved = localStorage.getItem('abet_preferences')
    if (saved) {
      try {
        return { ...DEFAULT_PREFERENCES, ...JSON.parse(saved) }
      } catch (e) {
        console.error('Failed to parse saved preferences:', e)
      }
    }
    return DEFAULT_PREFERENCES
  })

  // Save to localStorage whenever preferences change
  useEffect(() => {
    localStorage.setItem('abet_preferences', JSON.stringify(preferences))
  }, [preferences])

  // Individual setters
  const setSelectedBooks = (books) => {
    setPreferences(prev => ({ ...prev, selectedBooks: books }))
  }

  const setBookOrder = (order) => {
    setPreferences(prev => ({ ...prev, bookOrder: order }))
  }

  const setState = (state) => {
    setPreferences(prev => ({ ...prev, state }))
  }

  const setOddsFormat = (format) => {
    setPreferences(prev => ({ ...prev, oddsFormat: format }))
  }

  const setUnitSize = (size) => {
    setPreferences(prev => ({ ...prev, unitSize: size }))
  }

  const setBankroll = (amount) => {
    setPreferences(prev => ({ ...prev, bankroll: amount }))
  }

  const setNotifications = (notifications) => {
    setPreferences(prev => ({ ...prev, notifications: { ...prev.notifications, ...notifications } }))
  }

  const setTheme = (theme) => {
    setPreferences(prev => ({ ...prev, theme }))
  }

  const setCompactView = (compact) => {
    setPreferences(prev => ({ ...prev, compactView: compact }))
  }

  const setShowImpliedProbability = (show) => {
    setPreferences(prev => ({ ...prev, showImpliedProbability: show }))
  }

  const resetPreferences = () => {
    setPreferences(DEFAULT_PREFERENCES)
  }

  // Get available books for current state
  const getAvailableBooks = () => {
    const stateBooks = STATE_BOOKS[preferences.state] || STATE_BOOKS.DEFAULT
    return ALL_SPORTSBOOKS.filter(b => stateBooks.includes(b.id))
  }

  const value = {
    // Data
    preferences,
    allSportsbooks: ALL_SPORTSBOOKS,
    legalStates: LEGAL_STATES,

    // Derived
    selectedBooks: preferences.selectedBooks,
    bookOrder: preferences.bookOrder,
    state: preferences.state,
    oddsFormat: preferences.oddsFormat,
    unitSize: preferences.unitSize,
    bankroll: preferences.bankroll,
    notifications: preferences.notifications,
    theme: preferences.theme,
    compactView: preferences.compactView,
    showImpliedProbability: preferences.showImpliedProbability,

    // Methods
    setSelectedBooks,
    setBookOrder,
    setState,
    setOddsFormat,
    setUnitSize,
    setBankroll,
    setNotifications,
    setTheme,
    setCompactView,
    setShowImpliedProbability,
    resetPreferences,
    getAvailableBooks,
  }

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  )
}

export function usePreferences() {
  const context = useContext(PreferencesContext)
  if (!context) {
    throw new Error('usePreferences must be used within a PreferencesProvider')
  }
  return context
}

export { ALL_SPORTSBOOKS, LEGAL_STATES, STATE_BOOKS }
